import React, { useState, useCallback } from 'react';
import { GiftCode } from '../../types';
import {
    addGiftCode,
    updateGiftCode,
    deleteGiftCode,
    generateGiftCodeWithMod11
} from '../../services/giftCodeService';
import { logger } from '../../utils/logger';
import { useGiftCodes } from '../../hooks/useGiftCodes';
import { useGiftCodeSearch } from '../../hooks/useGiftCodeSearch';

// GiftCodeCardの定義。
// React.memo を削除しました
interface GiftCodeCardProps {
    giftCode: GiftCode;
    isEditing: boolean;
    current: Partial<GiftCode>;
    onChange: (id: string, field: keyof GiftCode, value: any) => void;
    onSave: (id: string) => void;
    onCancel: (id: string) => void;
    onEdit: (id: string) => void;
    onDelete: (id: string) => void;
}

const GiftCodeCard: React.FC<GiftCodeCardProps> = ({
    giftCode,
    isEditing,
    current,
    onChange,
    onSave,
    onCancel,
    onEdit,
    onDelete
}) => {
    const cardBgClass = giftCode.active ? 'bg-white' : 'bg-gray-200';

    return (
        <div className={`${cardBgClass} rounded-2xl shadow-lg group hover:-translate-y-2 transition-all relative`}>
            {!isEditing ? (
                <div className="block p-6 h-full flex flex-col">
                    <h3 className="text-xl font-bold text-gray-800 mb-2">ギフトコード: {giftCode.giftCode}</h3>
                    <p className="text-gray-600 text-sm mb-1">クイズID: {giftCode.QuizId}</p>
                    <p className="text-gray-600 text-sm mb-1">最大DL数: {giftCode.max_dl_count !== undefined ? giftCode.max_dl_count : '無制限'}</p>
                    <p className="text-gray-600 text-sm mb-1">現在のDL数: {giftCode.dl_count}</p>
                    <p className="text-gray-600 text-sm mb-1">ステータス: {giftCode.active ? '有効' : '無効'}</p>
                    <p className="text-gray-600 text-sm mb-1">許可ユーザー: {giftCode.allowed_users && giftCode.allowed_users.length > 0 ? giftCode.allowed_users.join(', ') : 'なし'}</p>
                    <p className="text-gray-600 text-sm mb-1">使用済みユーザー: {giftCode.used_by_user_id && giftCode.used_by_user_id.length > 0 ? giftCode.used_by_user_id.join(', ') : 'なし'}</p>
                </div>
            ) : (
                <div className="p-6 h-full flex flex-col bg-white border border-blue-300 rounded-2xl">
                    <h3 className="text-xl font-bold text-gray-800 mb-2">ギフトコードを編集</h3>
                    <div className="mb-2">
                        <label className="block text-sm font-bold text-gray-700">ギフトコード (編集不可)</label>
                        <input
                            className="border p-2 rounded w-full text-gray-800 bg-gray-100"
                            value={giftCode.giftCode}
                            disabled
                        />
                    </div>
                    <div className="mb-2">
                        <label className="block text-sm font-bold text-gray-700">クイズID</label>
                        <input
                            className="border p-2 rounded w-full text-gray-800"
                            value={current.QuizId ?? ''}
                            onChange={(e) => onChange(giftCode.giftCode, 'QuizId', e.target.value)}
                        />
                    </div>
                    <div className="mb-2">
                        <label className="block text-sm font-bold text-gray-700">最大DL数 (空欄で無制限)</label>
                        <input
                            type="number"
                            className="border p-2 rounded w-full text-gray-800"
                            value={current.max_dl_count ?? ''}
                            onChange={(e) => onChange(giftCode.giftCode, 'max_dl_count', e.target.value === '' ? undefined : parseInt(e.target.value))}
                        />
                    </div>
                    <div className="mb-2">
                        <label className="block text-sm font-bold text-gray-700">ステータス</label>
                        <select
                            className="border p-2 rounded w-full text-gray-800"
                            value={current.active?.toString() ?? ''}
                            onChange={(e) => onChange(giftCode.giftCode, 'active', e.target.value === 'true')}
                        >
                            <option value="true">有効</option>
                            <option value="false">無効</option>
                        </select>
                    </div>
                    <div className="mb-2">
                        <label className="block text-sm font-bold text-gray-700">許可ユーザー (カンマ区切り)</label>
                        <input
                            className="border p-2 rounded w-full text-gray-800"
                            value={current.allowed_users?.join(', ') ?? ''}
                            onChange={(e) => onChange(giftCode.giftCode, 'allowed_users', e.target.value.split(',').map((s: string) => s.trim()).filter((s: string) => s !== ''))}
                        />
                    </div>
                    <div className="mb-2">
                        <label className="block text-sm font-bold text-gray-700">現在のDL数</label>
                        <input
                            className="border p-2 rounded w-full text-gray-800 bg-gray-100"
                            value={giftCode.dl_count}
                            disabled
                        />
                    </div>
                    <div className="mb-2">
                        <label className="block text-sm font-bold text-gray-700">使用済みユーザー</label>
                        <input
                            className="border p-2 rounded w-full text-gray-800 bg-gray-100"
                            value={giftCode.used_by_user_id?.join(', ') ?? ''}
                            disabled
                        />
                    </div>
                </div>
            )}

            <div className="absolute bottom-4 right-4 flex gap-2">
                {isEditing ? (
                    <>
                        <button onClick={() => onSave(giftCode.giftCode)} className="bg-blue-500 text-white px-3 py-1 rounded-lg text-sm hover:bg-blue-600 transition">保存</button>
                        <button onClick={() => onCancel(giftCode.giftCode)} className="bg-gray-300 text-gray-800 px-3 py-1 rounded-lg text-sm hover:bg-gray-400 transition">取消</button>
                    </>
                ) : (
                    <>
                        <button onClick={() => onEdit(giftCode.giftCode)} className="bg-green-500 text-white px-3 py-1 rounded-lg text-sm hover:bg-green-600 transition">編集</button>
                        <button onClick={() => onDelete(giftCode.giftCode)} className="bg-red-500 text-white px-3 py-1 rounded-lg text-sm hover:bg-red-600 transition">無効化</button>
                    </>
                )}
            </div>
        </div>
    );
};

interface DialogConfig {
    message: string | null;
    onConfirm?: () => void;
    onCancel?: () => void;
    showCancel?: boolean;
}

interface GiftCodeManagerProps {
}

const GiftCodeManager: React.FC<GiftCodeManagerProps> = () => {
    const { giftCodes: allGiftCodes, loading: isLoading, error } = useGiftCodes();

    const {
        filteredGiftCodes,
        searchActive,
        setSearchActive,
        searchGiftCode,
        setSearchGiftCode,
        searchMaxDlCountExists,
        setSearchMaxDlCountExists,
        searchAllowedUsersExists,
        setSearchAllowedUsersExists
    } = useGiftCodeSearch(allGiftCodes);

    const [editStates, setEditStates] = useState<{ [key: string]: Partial<GiftCode> }>({});

    const [newGiftCodeData, setNewGiftCodeData] = useState<Omit<GiftCode, 'dl_count' | 'active' | 'giftCode'>>({
        QuizId: '',
        max_dl_count: undefined,
        allowed_users: undefined,
    });
    const [isGeneratingNewCode, setIsGeneratingNewCode] = useState<boolean>(false);
    const [dialogConfig, setDialogConfig] = useState<DialogConfig>({ message: null });

    const showMessage = useCallback(
        (message: string, onConfirm: () => void = () => { }, onCancel: () => void = () => { }, showCancel: boolean = true) => {
            setDialogConfig({
                message,
                onConfirm: () => { onConfirm(); setDialogConfig({ message: null }); },
                onCancel: () => { onCancel(); setDialogConfig({ message: null }); },
                showCancel
            });
        }, []);

    const handleEditChange = (id: string, field: keyof GiftCode, value: any) => {
        setEditStates(prev => {
            const currentEdit = prev[id] || {};
            return {
                ...prev,
                [id]: {
                    ...currentEdit,
                    [field]: value,
                }
            };
        });
    };

    const handleEditSave = async (id: string) => {
        const updated = editStates[id];
        if (!updated.QuizId) {
            showMessage('クイズIDは必須です。', () => { }, () => { }, false);
            return;
        }

        try {
            await updateGiftCode(id, updated);
            setEditStates(prev => {
                const copy = { ...prev };
                delete copy[id];
                return copy;
            });
            showMessage('ギフトコードが正常に更新されました。', () => { }, () => { }, false);
        } catch (err: any) {
            logger.error('Failed to update gift code:', err);
            showMessage('ギフトコードの更新に失敗しました: ' + err.message, () => { }, () => { }, false);
        }
    };

    const handleEditCancel = (id: string) => {
        setEditStates(prev => {
            const copy = { ...prev };
            delete copy[id];
            return copy;
        });
    };

    const handleDelete = async (id: string) => {
        showMessage(
            `ギフトコード '${id}' を無効化しますか？`,
            async () => {
                try {
                    await deleteGiftCode(id);
                    showMessage('ギフトコードを無効化しました。', () => { }, () => { }, false);
                } catch (err: any) {
                    logger.error('Failed to deactivate gift code:', err);
                    showMessage('ギフトコードの無効化に失敗しました: ' + err.message, () => { }, () => { }, false);
                }
            },
            undefined,
            true
        );
    };

    const handleAddNewGiftCode = async () => {
        if (!newGiftCodeData.QuizId) {
            showMessage('クイズIDは必須です。', () => { }, () => { }, false);
            return;
        }

        try {
            setIsGeneratingNewCode(true);
            const generatedGiftCode = generateGiftCodeWithMod11();
            logger.log("Generated new gift code:", generatedGiftCode);

            const addedGiftCode = await addGiftCode({
                giftCode: generatedGiftCode,
                QuizId: newGiftCodeData.QuizId,
                max_dl_count: newGiftCodeData.max_dl_count,
                allowed_users: newGiftCodeData.allowed_users,
            });
            setNewGiftCodeData({
                QuizId: '',
                max_dl_count: undefined,
                allowed_users: undefined,
            });
            showMessage(`新しいギフトコード '${addedGiftCode.giftCode}' が正常に追加されました！`, () => { }, () => { }, false);
        } catch (err: any) {
            logger.error('Failed to add new gift code:', err);
            showMessage('新しいギフトコードの追加に失敗しました: ' + err.message, () => { }, () => { }, false);
        } finally {
            setIsGeneratingNewCode(false);
        }
    };

    if (isLoading) {
        return <p className="text-center py-4 text-gray-500">ギフトコードデータを読み込み中...</p>;
    }

    if (error) {
        return <p className="text-center py-4 text-red-500">エラー: {error.message}</p>;
    }

    return (
        <div className="p-6 max-w-6xl mx-auto">
            <h2 className="text-3xl font-extrabold text-center text-gradient from-purple-500 to-pink-400 mb-8">
                ギフトコード管理ダッシュボード
            </h2>

            {/* 検索フィルター */}
            <div className="mb-6 p-4 bg-white rounded-lg shadow-md grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                    <label htmlFor="searchActive" className="block text-sm font-bold text-gray-700 mb-1">ステータス:</label>
                    <select
                        id="searchActive"
                        className="border p-2 rounded w-full text-gray-800"
                        value={searchActive}
                        onChange={(e) => setSearchActive(e.target.value)}
                    >
                        <option value="all">全て</option>
                        <option value="true">有効</option>
                        <option value="false">無効</option>
                    </select>
                </div>
                <div>
                    <label htmlFor="searchGiftCode" className="block text-sm font-bold text-gray-700 mb-1">ギフトコード:</label>
                    <input
                        type="text"
                        id="searchGiftCode"
                        className="border p-2 rounded w-full text-gray-800"
                        value={searchGiftCode}
                        onChange={(e) => setSearchGiftCode(e.target.value)}
                        placeholder="コードを入力"
                    />
                </div>
                <div>
                    <label htmlFor="searchMaxDlCountExists" className="block text-sm font-bold text-gray-700 mb-1">最大DL数:</label>
                    <select
                        id="searchMaxDlCountExists"
                        className="border p-2 rounded w-full text-gray-800"
                        value={searchMaxDlCountExists}
                        onChange={(e) => setSearchMaxDlCountExists(e.target.value)}
                    >
                        <option value="all">全て</option>
                        <option value="true">制限あり</option>
                        <option value="false">無制限</option>
                    </select>
                </div>
                <div>
                    <label htmlFor="searchAllowedUsersExists" className="block text-sm font-bold text-gray-700 mb-1">許可ユーザー:</label>
                    <select
                        id="searchAllowedUsersExists"
                        className="border p-2 rounded w-full text-gray-800"
                        value={searchAllowedUsersExists}
                        onChange={(e) => setSearchAllowedUsersExists(e.target.value)}
                    >
                        <option value="all">全て</option>
                        <option value="true">制限あり</option>
                        <option value="false">制限なし</option>
                    </select>
                </div>
            </div>

            {/* 検索結果表示 */}
            <div className={`grid grid-cols-1 ${filteredGiftCodes.length > 1 ? 'sm:grid-cols-2 lg:grid-cols-3' : ''} gap-6 mb-12`}>
                {filteredGiftCodes.length > 0 ? (
                    filteredGiftCodes.map((gc: GiftCode) => {
                        const isEditing = !!editStates[gc.giftCode];
                        const current = isEditing ? editStates[gc.giftCode] : gc;

                        return (
                            <GiftCodeCard
                                key={gc.giftCode}
                                giftCode={gc}
                                isEditing={isEditing}
                                current={current}
                                onChange={handleEditChange}
                                onSave={handleEditSave}
                                onCancel={handleEditCancel}
                                onEdit={(id) => setEditStates(prev => ({ ...prev, [id]: { ...gc } }))}
                                onDelete={handleDelete}
                            />
                        );
                    })
                ) : (
                    <p className="col-span-full text-center text-gray-500 py-8">
                        {isLoading ? '読み込み中...' : '該当するギフトコードは見つかりませんでした。'}
                    </p>
                )}
            </div>

            {/* 新規追加フォーム */}
            <div className="bg-purple-50 p-6 rounded-xl shadow-inner border border-purple-200">
                <h3 className="text-xl font-bold text-purple-600 mb-4">新しいギフトコードを追加</h3>
                <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                        <label className="text-sm font-bold block">クイズID <span className="text-red-500">*</span></label>
                        <input
                            className="border p-2 rounded w-full"
                            value={newGiftCodeData.QuizId}
                            onChange={(e) => setNewGiftCodeData(prev => ({ ...prev, QuizId: e.target.value }))}
                            placeholder="紐づけるクイズID"
                        />
                    </div>
                    <div>
                        <label className="text-sm font-bold block">最大DL数 (空欄で無制限)</label>
                        <input
                            type="number"
                            className="border p-2 rounded w-full"
                            value={newGiftCodeData.max_dl_count ?? ''}
                            onChange={(e) => setNewGiftCodeData(prev => ({ ...prev, max_dl_count: e.target.value === '' ? undefined : parseInt(e.target.value) }))}
                            placeholder="例: 10"
                        />
                    </div>
                    <div className="sm:col-span-2">
                        <label className="block text-sm font-bold text-gray-700">許可ユーザー (カンマ区切り、空欄で制限なし)</label>
                        <input
                            className="border p-2 rounded w-full"
                            value={newGiftCodeData.allowed_users?.join(', ') ?? ''}
                            onChange={(e) => setNewGiftCodeData(prev => ({ ...prev, allowed_users: e.target.value.split(',').map(s => s.trim()).filter(s => s !== '') }))}
                            placeholder="例: user1, user2"
                        />
                    </div>
                </div>
                <div className="mt-4">
                    <button
                        onClick={handleAddNewGiftCode}
                        className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-6 py-2 rounded-lg font-bold hover:from-purple-600 hover:to-pink-600 transition-colors disabled:opacity-50"
                        disabled={isGeneratingNewCode || !newGiftCodeData.QuizId.trim()}
                    >
                        {isGeneratingNewCode ? '生成中...' : '新しいギフトコードを生成して追加'}
                    </button>
                </div>
            </div>

            {/* カスタムダイアログの表示 */}
            {dialogConfig.message && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex justify-center items-center z-50">
                    <div className="bg-white p-6 rounded-lg shadow-xl max-w-sm mx-auto">
                        <p className="text-gray-800 text-lg mb-4">{dialogConfig.message}</p>
                        <div className="flex justify-end space-x-3">
                            {dialogConfig.showCancel && (
                                <button
                                    onClick={dialogConfig.onCancel}
                                    className="px-4 py-2 bg-gray-300 text-gray-800 rounded-lg hover:bg-gray-400 transition"
                                >
                                    キャンセル
                                </button>
                            )}
                            <button
                                onClick={dialogConfig.onConfirm}
                                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition"
                            >
                                OK
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default GiftCodeManager;
